import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as AutoFixHighIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Publish as PublishIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import type { Schema } from '../../services/api';
import GuidDisplay from '../common/GuidDisplay';
import { formatTimestamp } from '../../utils/dataUtils';

interface SchemaDraftTableProps {
  drafts: Schema[];
  isLoading: boolean;
  onViewDetails: (schema: Schema) => void;
  onEdit: (schema: Schema) => void;
  onAnnotate: (schema: Schema) => void;
  onPromote: (schema: Schema) => void;
  onDelete: (schema: Schema) => void;
  onUpload: () => void;
}

const SchemaDraftTable: React.FC<SchemaDraftTableProps> = ({
  drafts,
  isLoading,
  onViewDetails,
  onEdit,
  onAnnotate,
  onPromote,
  onDelete,
  onUpload,
}) => (
  <>
    <Box display="flex" justifyContent="space-between" mb={2}>
      <Typography variant="body2" color="text.secondary">
        Upload and annotate schemas before promoting them
      </Typography>
      <Button variant="contained" startIcon={<UploadIcon />} onClick={onUpload}>
        Upload Schema
      </Button>
    </Box>

    {isLoading ? (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Tables</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drafts.map((schema) => {
              const displayName = schema.schema_name || schema.definition?.schema_name || '';
              return (
                <TableRow key={schema.schema_id} hover>
                  <TableCell>
                    <Stack spacing={0.3}>
                      <Typography variant="body2" fontWeight={600}>
                        {displayName || 'Untitled Schema'}
                      </Typography>
                      <GuidDisplay guid={schema.schema_id} abbreviate />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip label={schema.status} size="small" />
                      {schema.definition?.needs_interpretation && (
                        <Chip
                          label={
                            schema.definition?.template_summary?.xsd_metadata
                              ? 'XSD — awaiting interpretation'
                              : 'Awaiting interpretation'
                          }
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{schema.source_type}</TableCell>
                  <TableCell>
                    {(() => {
                      const tables = schema.definition?.tables || {};
                      return Object.keys(tables).length;
                    })()}
                  </TableCell>
                  <TableCell>{formatTimestamp(schema.created_at)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => onViewDetails(schema)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit descriptions and rules">
                        <IconButton size="small" onClick={() => onEdit(schema)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={schema.definition?.needs_interpretation ? "Annotate template" : "Annotate schema"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onAnnotate(schema)}
                          >
                            <AutoFixHighIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Promote to Schema">
                        <IconButton size="small" onClick={() => onPromote(schema)}>
                          <PublishIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete draft">
                        <IconButton size="small" color="error" onClick={() => onDelete(schema)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </>
);

export default SchemaDraftTable;
